import { useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFestivalTheme } from '../contexts/FestivalThemeContext';

// Snow particle component - starts from very top of screen (above safe area)
function SnowParticle({ index }: { index: number }) {
  const randomX = useMemo(() => Math.random() * 100, []);
  const randomDelay = useMemo(() => Math.random() * 10, []);
  const randomDuration = useMemo(() => 15 + Math.random() * 10, []);
  const randomSize = useMemo(() => 4 + Math.random() * 6, []);
  const randomOpacity = useMemo(() => 0.4 + Math.random() * 0.4, []);

  return (
    <motion.div
      className="fixed pointer-events-none select-none rounded-full bg-white"
      style={{
        left: `${randomX}%`,
        // Start above the screen (negative value) to appear from status bar
        top: -20,
        width: randomSize,
        height: randomSize,
        opacity: randomOpacity,
        boxShadow: '0 0 4px rgba(255,255,255,0.5)',
        zIndex: 9999, // High z-index to appear above everything
      }}
      animate={{
        y: [0, (typeof window !== 'undefined' ? window.innerHeight : 800) + 40],
        x: [0, Math.sin(index) * 30, 0],
      }}
      transition={{
        duration: randomDuration,
        delay: randomDelay,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  );
}

// Heart particle for Valentine's - rises from bottom
function HeartParticle({ index }: { index: number }) {
  const randomX = useMemo(() => Math.random() * 100, []);
  const randomDelay = useMemo(() => Math.random() * 8, []);
  const randomDuration = useMemo(() => 12 + Math.random() * 8, []);
  const randomSize = useMemo(() => 0.6 + Math.random() * 0.4, []);

  return (
    <motion.div
      className="fixed pointer-events-none select-none"
      style={{
        left: `${randomX}%`,
        bottom: 80,
        fontSize: `${randomSize}rem`,
        opacity: 0.4,
        zIndex: 9999,
        color: '#ec4899',
      }}
      animate={{
        y: [0, -(typeof window !== 'undefined' ? window.innerHeight : 600) - 50],
        opacity: [0, 0.5, 0.4, 0],
      }}
      transition={{
        duration: randomDuration,
        delay: randomDelay,
        repeat: Infinity,
        ease: 'easeOut',
      }}
    >
      ♥
    </motion.div>
  );
}

// Confetti/Star particle - falls from top
function ConfettiParticle({ index, color }: { index: number; color: string }) {
  const randomX = useMemo(() => Math.random() * 100, []);
  const randomDelay = useMemo(() => Math.random() * 10, []);
  const randomDuration = useMemo(() => 12 + Math.random() * 8, []);
  const randomSize = useMemo(() => 4 + Math.random() * 5, []);

  return (
    <motion.div
      className="fixed pointer-events-none select-none rounded-full"
      style={{
        left: `${randomX}%`,
        top: -20,
        width: randomSize,
        height: randomSize,
        backgroundColor: color,
        opacity: 0.5,
        zIndex: 9999,
      }}
      animate={{
        y: [0, (typeof window !== 'undefined' ? window.innerHeight : 800) + 40],
        x: [0, Math.sin(index * 2) * 40, 0],
        rotate: [0, 360],
      }}
      transition={{
        duration: randomDuration,
        delay: randomDelay,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  );
}

// Leaf particle for fall/thanksgiving - falls from top
function LeafParticle({ index }: { index: number }) {
  const randomX = useMemo(() => Math.random() * 100, []);
  const randomDelay = useMemo(() => Math.random() * 8, []);
  const randomDuration = useMemo(() => 10 + Math.random() * 8, []);
  const leaves = ['🍂', '🍁'];

  return (
    <motion.div
      className="fixed pointer-events-none select-none text-lg"
      style={{
        left: `${randomX}%`,
        top: -30,
        opacity: 0.6,
        zIndex: 9999,
      }}
      animate={{
        y: [0, (typeof window !== 'undefined' ? window.innerHeight : 800) + 50],
        x: [0, Math.sin(index) * 60, 0],
        rotate: [0, 360],
      }}
      transition={{
        duration: randomDuration,
        delay: randomDelay,
        repeat: Infinity,
        ease: 'linear',
      }}
    >
      {leaves[index % 2]}
    </motion.div>
  );
}

// Refined Festival Banner that extends into status bar with seamless gradient
export function FestivalBanner() {
  const { currentTheme, currentThemeId } = useFestivalTheme();

  // Update the theme-color meta tag for status bar
  useEffect(() => {
    if (currentThemeId === 'none') {
      // Reset to default
      updateThemeColor('#ffffff');
      return;
    }
    
    // Set status bar color to match the TOP of the gradient (gradientFrom)
    // This creates seamless look as gradient starts from this color
    updateThemeColor(currentTheme.gradientFrom);
    
    return () => {
      // Reset when component unmounts or theme changes to none
      updateThemeColor('#ffffff');
    };
  }, [currentThemeId, currentTheme.gradientFrom]);

  if (currentThemeId === 'none') return null;

  return (
    <div 
      className="relative w-full"
      style={{
        // The entire container has the gradient - this is key!
        background: `linear-gradient(180deg, ${currentTheme.gradientFrom} 0%, ${currentTheme.gradientTo} 100%)`,
      }}
    >
      {/* This div fills the safe area (status bar) with the SAME gradient */}
      <div 
        style={{
          // This creates space for the status bar content
          // The gradient background from parent shows through
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      />
      
      {/* Banner content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="relative text-white text-center py-2.5 px-4"
      >
        {/* Subtle shine effect */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
          }}
        />
        
        {/* Content */}
        <span className="relative z-10 flex items-center justify-center gap-2 text-sm font-medium">
          <span>{currentTheme.heroBadgeEmoji}</span>
          <span>{currentTheme.heroBadgeText}</span>
          <span>{currentTheme.heroBadgeEmoji}</span>
        </span>
      </motion.div>
      
      {/* Gradient fade at bottom - creates soft edge into content */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-8 translate-y-full pointer-events-none"
        style={{
          background: `linear-gradient(to bottom, ${currentTheme.gradientTo}30, transparent)`,
        }}
      />
    </div>
  );
}

// Helper function to update theme-color meta tag (for status bar)
function updateThemeColor(color: string) {
  // Update existing meta tag or create new one
  let metaThemeColor = document.querySelector('meta[name="theme-color"]');
  
  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta');
    metaThemeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(metaThemeColor);
  }
  
  metaThemeColor.setAttribute('content', color);
  
  // Also update apple-mobile-web-app-status-bar-style for iOS
  let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
  
  if (!metaStatusBar) {
    metaStatusBar = document.createElement('meta');
    metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
    document.head.appendChild(metaStatusBar);
  }
  
  // Use 'black-translucent' for colored status bar on iOS
  metaStatusBar.setAttribute('content', color === '#ffffff' ? 'default' : 'black-translucent');
}

// Main Festival Decorations - Particles only, no UI changes
export function FestivalDecorations({ 
  particleCount = 12,
}: {
  particleCount?: number;
}) {
  const { currentTheme, currentThemeId } = useFestivalTheme();

  if (currentThemeId === 'none') return null;

  const renderParticles = () => {
    switch (currentTheme.particleType) {
      case 'snow':
        return Array.from({ length: particleCount }).map((_, i) => (
          <SnowParticle key={i} index={i} />
        ));
      case 'hearts':
        return Array.from({ length: Math.min(particleCount, 8) }).map((_, i) => (
          <HeartParticle key={i} index={i} />
        ));
      case 'confetti':
      case 'fireworks':
      case 'stars':
        return Array.from({ length: particleCount }).map((_, i) => (
          <ConfettiParticle 
            key={i} 
            index={i} 
            color={i % 2 === 0 ? currentTheme.primaryColor : currentTheme.secondaryColor} 
          />
        ));
      case 'leaves':
        return Array.from({ length: Math.min(particleCount, 8) }).map((_, i) => (
          <LeafParticle key={i} index={i} />
        ));
      default:
        return null;
    }
  };

  return (
    <>
      {/* Particles rendered as fixed elements covering entire viewport including status bar */}
      {renderParticles()}
    </>
  );
}

// Simple hook to check if festival theme is active
export function useIsFestivalActive() {
  const { currentThemeId } = useFestivalTheme();
  return currentThemeId !== 'none';
}

export default FestivalDecorations;
